// ProjectThroughputReport.jsx

import React, { useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { endOfDay, startOfDay, startOfYear } from 'date-fns';
import { normalizeDateValue, timestampFromDate } from 'utilities/DateTime';
import {
  buildProjectWorkflowReportIndex,
  buildThroughputSummary,
  formatAgeLabel,
} from 'collection/reports/project/projectWorkflowReportIndex';

// CSS
import './ProjectWorkflowReportShared.css';

// Components
import NodeDateInput from 'node/form/fields/date/NodeDateInput';
import SelectInput from 'components/selectinput/SelectInput';

const DATE_FRIENDLY_FORMAT = Object.freeze({
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const toDateFieldValue = (date) => {
  if (!date) return null;
  return {
    endDate: null,
    endPrecision: null,
    isRange: false,
    startDate: timestampFromDate(date),
    startPrecision: 'date',
  };
};

const fromDateFieldValue = (value) => {
  if (!value?.startDate) return null;
  return normalizeDateValue(value.startDate);
};

/**
 * ProjectThroughputReport
 *
 * Overview widget: launches, completions, cycle time, outcome mix.
 */
const ProjectThroughputReport = ({
  nodes = [],
  isLoading = false,
  title = 'Throughput',
  description = 'Playbook launches and cycle time for the selected range.',
}) => {
  const { theme } = useTheme();
  const [templateKey, setTemplateKey] = useState('');
  const [rangeStart, setRangeStart] = useState(() => startOfYear(new Date()));
  const [rangeEnd, setRangeEnd] = useState(() => new Date());

  const index = useMemo(
    () => buildProjectWorkflowReportIndex({ nodes }),
    [nodes],
  );

  const summary = useMemo(() => (
    buildThroughputSummary(index.runSummaries, {
      rangeEnd: rangeEnd ? endOfDay(rangeEnd) : null,
      rangeStart: rangeStart ? startOfDay(rangeStart) : null,
      templateKey,
    })
  ), [index.runSummaries, rangeEnd, rangeStart, templateKey]);

  const templateOptions = useMemo(() => ([
    { label: 'All playbooks', value: '' },
    ...index.templateOptions,
  ]), [index.templateOptions]);

  return (
    <div className="project-workflow-report">
      <div className="project-workflow-report-intro">
        <div
          className="project-workflow-report-title"
          style={{ color: theme.foreground }}
        >
          {title}
        </div>
        <div
          className="project-workflow-report-description"
          style={{ color: theme.foregroundMuted }}
        >
          {description}
        </div>
      </div>

      <div className="project-workflow-report-toolbar">
        <div className="project-workflow-report-toolbar-group">
          <div
            className="project-workflow-report-filter-label"
            style={{ color: theme.foregroundMuted }}
          >
            Launched
          </div>
          <div className="project-workflow-report-date-row">
            <div className="project-workflow-report-date-control">
              <NodeDateInput
                className="project-workflow-report-date-input"
                friendlyFormatOptions={DATE_FRIENDLY_FORMAT}
                iconSize={18}
                value={toDateFieldValue(rangeStart)}
                onChange={(next) => setRangeStart(fromDateFieldValue(next))}
              />
            </div>
            <span style={{ color: theme.foregroundMuted, fontSize: 13 }}>to</span>
            <div className="project-workflow-report-date-control">
              <NodeDateInput
                className="project-workflow-report-date-input"
                friendlyFormatOptions={DATE_FRIENDLY_FORMAT}
                iconSize={18}
                value={toDateFieldValue(rangeEnd)}
                onChange={(next) => setRangeEnd(fromDateFieldValue(next))}
              />
            </div>
          </div>
        </div>

        <div className="project-workflow-report-toolbar-group">
          <div
            className="project-workflow-report-filter-label"
            style={{ color: theme.foregroundMuted }}
          >
            Playbook
          </div>
          <div className="project-workflow-report-filter-select">
            <SelectInput
              height="44px"
              options={templateOptions}
              showIcons={false}
              value={templateKey}
              width="100%"
              onChange={(event) => setTemplateKey(event?.target?.value || '')}
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div
          className="project-workflow-report-empty"
          style={{ color: theme.foregroundMuted }}
        >
          Loading…
        </div>
      ) : summary.launchedCount === 0 ? (
        <div
          className="project-workflow-report-empty"
          style={{ color: theme.foregroundMuted }}
        >
          No playbook runs launched in this range.
        </div>
      ) : (
        <>
          <div className="project-workflow-report-metrics">
            <div className="project-workflow-report-metric">
              <div
                className="project-workflow-report-metric-label"
                style={{ color: theme.foregroundMuted }}
              >
                Launched
              </div>
              <div
                className="project-workflow-report-metric-value"
                style={{ color: theme.foreground }}
              >
                {summary.launchedCount}
              </div>
            </div>
            <div className="project-workflow-report-metric">
              <div
                className="project-workflow-report-metric-label"
                style={{ color: theme.foregroundMuted }}
              >
                Completed
              </div>
              <div
                className="project-workflow-report-metric-value"
                style={{ color: theme.foreground }}
              >
                {summary.completedCount}
              </div>
              <div
                className="project-workflow-report-metric-note"
                style={{ color: theme.foregroundMuted }}
              >
                {summary.openCount} still open
              </div>
            </div>
            <div className="project-workflow-report-metric">
              <div
                className="project-workflow-report-metric-label"
                style={{ color: theme.foregroundMuted }}
              >
                Median cycle
              </div>
              <div
                className="project-workflow-report-metric-value"
                style={{ color: theme.foreground }}
              >
                {summary.cycleTimeMedianMs != null
                  ? formatAgeLabel(summary.cycleTimeMedianMs)
                  : '—'}
              </div>
              <div
                className="project-workflow-report-metric-note"
                style={{ color: theme.foregroundMuted }}
              >
                {summary.cycleTimeP90Ms != null
                  ? `p90 ${formatAgeLabel(summary.cycleTimeP90Ms)}`
                  : 'No completions yet'}
              </div>
            </div>
          </div>

          {summary.outcomeMix.length > 0 ? (
            <div className="project-workflow-report-mix">
              <div className="project-workflow-report-mix-bar" aria-hidden="true">
                {summary.outcomeMix.map((segment) => (
                  <div
                    className="project-workflow-report-mix-segment"
                    key={segment.key}
                    style={{ flexBasis: 0, flexGrow: segment.count }}
                  />
                ))}
              </div>
              <div className="project-workflow-report-mix-legend">
                {summary.outcomeMix.map((segment) => (
                  <div
                    className="project-workflow-report-mix-legend-item"
                    key={segment.key}
                    style={{ color: theme.foregroundMuted }}
                  >
                    <span className="project-workflow-report-mix-swatch" />
                    <span>
                      {`${segment.label} ${Math.round(segment.ratio * 100)}%`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

ProjectThroughputReport.displayName = 'ProjectThroughputReport';

export default ProjectThroughputReport;
